import {
  API_ROUTE_STATE_CODE_PLACEHOLDER,
  ORIGIN_TYPES,
  SERVICE_TYPE,
  USER_TYPES,
} from '../constants/common';
import { BRAND_CODES, getBrandContext } from '../brand';
import { resolveDomainToken, DOMAIN_PLACEHOLDER } from './domainResolver';

export const redirectToExternalUrl = (publicLink: string): void => {
  window.open(publicLink, '_self');
};

export const getStateCodeFromParsedUrl = (): string => {
  const { brandCode } = getBrandContext();

  // HealthSpring doesn't have state codes — return empty string
  if (brandCode === BRAND_CODES.HEALTHSPRING) {
    return '';
  }

  // BCBS multi-state extraction
  const url = window && window.location && window.location.href;
  const secondLevelLocation = url.includes('bcbs')
    ? url.split('bcbs')[1]
    : 'IL';
  return secondLevelLocation.split('.')[0].toUpperCase();
};

/** * Loads runtime config synchronously from nginx /config endpoint. * Uses sync XHR so it runs before any module-level code reads config values. * Call this at the very top of index.tsx before any other imports that * depend on config (or let the module auto-load it on first use). */
const loadAppConfigSync = (): Record<string, string> => {
  try {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', '/gifnoc', false); // false = synchronous
    xhr.send();
    if (xhr.status === 200) {
      return JSON.parse(xhr.responseText);
    }
  } catch {
    // local dev — /config not served by nginx, fall back to process.env
  }
  return {};
};

// Populated once at module load time — synchronous so module-level
// constants like PROCESS_ENV_URLS and baseURL in utils.ts get real values.
const _appConfig: Record<string, string> = loadAppConfigSync();

const getConfigValue = (key: string): string => {
  return _appConfig[key] ?? process.env[key] ?? '';
};

export const getStateCodeSpecificUrlUsingProcessEnv = () => {
  const { brandCode } = getBrandContext();

  // Domain token for {domain} template substitution:
  //   BCBS       → 'bcbsil', 'bcbstx', etc.
  //   HealthSpring → 'healthspring'
  const domain = resolveDomainToken();

  // Legacy state-code for ##STATE_CODE## backward-compat
  const stateCode = getStateCodeFromParsedUrl().toLowerCase();

  /**
   * Resolve a URL env var, supporting two template patterns in priority order:
   *  1. `{domain}` — new canonical template (works for both BCBS and HealthSpring)
   *  2. `##STATE_CODE##` — legacy BCBS-only pattern (backward compat)
   */
  const resolveUrl = (key: string): string => {
    const raw = getConfigValue(key);

    // 1. New template: {domain}
    if (raw.includes(DOMAIN_PLACEHOLDER)) {
      return raw.split(DOMAIN_PLACEHOLDER).join(domain);
    }

    // 2. Legacy ##STATE_CODE## substitution (BCBS env files not yet migrated)
    return raw.replace(new RegExp(API_ROUTE_STATE_CODE_PLACEHOLDER, 'g'), stateCode);
  };

  // --- Non-URL / static env vars (no template substitution needed) ---
  const scope         = getConfigValue('REACT_APP_FORGEROCK_SCOPE');
  const sessionKey    = getConfigValue('REACT_APP_SESSION_KEY');
  const bamredirectUrl = getConfigValue('REACT_APP_BAM_REDIRECT_URL');
  const iframeUrlCard = getConfigValue('REACT_APP_IFRAME_URL_CARD');
  const iframeUrlBank = getConfigValue('REACT_APP_IFRAME_URL_BANK');
  const fiservIFrame  = getConfigValue('REACT_APP_IFRAME_FUNDING_URL');
  const adobeAnalyticsURL = getConfigValue('REACT_APP_ADOBE_ANALYTICS_URL');
  const healthSpringService =
    getConfigValue('REACT_APP_FORGEROCK_SERVICE_HEALTHSPRING') ||
    'hcsc-healthspring-members-login';
  const healthSpringLocale =
    getConfigValue('REACT_APP_FORGEROCK_LOCALE_HEALTHSPRING') || 'HS';

  // --- clientId: brand-aware, HealthSpring prefers its own client id ---
  const clientId =
    brandCode === BRAND_CODES.HEALTHSPRING
      ? getConfigValue('REACT_APP_FORGEROCK_CLIENT_ID_HEALTHSPRING') ||
        getConfigValue('REACT_APP_FORGEROCK_CLIENT_ID')
      : getConfigValue('REACT_APP_FORGEROCK_CLIENT_ID');

  // --- URL env vars resolved via template or legacy fallback ---
  const baseURL        = resolveUrl('REACT_APP_API_HOST');
  const exitURL        = resolveUrl('REACT_APP_EXIT_LINK');
  const spanishBcbsURL = resolveUrl('REACT_APP_MOTIONPOINT_LINK');
  const publicSiteRedirectUri = resolveUrl('REACT_APP_PUBLICSITE_HOMEPAGE');
  const publicSiteRedirectUriForGovCA = resolveUrl('REACT_APP_PUBLICSITE_FOR_GOVT_CA');
  const medicareURL    = resolveUrl('REACT_APP_MEDICARE_URL');

  // ForgeRock / OAuth URLs — all share the same domain template pattern
  const clientCaId          = getConfigValue('REACT_APP_FORGEROCK_CA_CLIENT_ID');
  const client_secret       = getConfigValue('REACT_APP_FORGEROCK_CLIENT_SECRET_ID');
  const authorizeUri        = resolveUrl('REACT_APP_FORGEROCK_AUTHORIZE_URI');
  const authorizeUriForCa   = resolveUrl('REACT_APP_FORGEROCK_CA_AUTHORIZE_URI');
  const accessTokenUri      = resolveUrl('REACT_APP_FORGEROCK_ACCESS_TOKEN_URI');
  const accessTokenUriForCa = resolveUrl('REACT_APP_FORGEROCK_CA_ACCESS_TOKEN_URI');
  const revokeAccessTokenUri = resolveUrl('REACT_APP_REVOKE_ACCESS_TOKEN_URI');
  const logoutActiveMemberUri = resolveUrl('REACT_APP_FORGEROCK_LOGOUT_URI');
  const HPSRedirectUri      = resolveUrl('REACT_APP_FORGEROCK_HPS_REDIRECT_URI');
  const userInfoURL         = resolveUrl('REACT_APP_FORGEROCK_USERINFO');

  return {
    // Static
    scope,
    sessionKey,
    bamredirectUrl,
    iframeUrlCard,
    iframeUrlBank,
    fiservIFrame,
    adobeAnalyticsURL,
    healthSpringService,
    healthSpringLocale,
    // Client IDs
    clientId,           // brand-resolved: correct value for current brand
    clientCaId,
    client_secret,
    // URL vars
    baseURL,            // brand-resolved
    exitURL,
    spanishBcbsURL,
    publicSiteRedirectUri,
    publicSiteRedirectUriForGovCA,
    medicareURL,
    // OAuth/ForgeRock URLs (brand-resolved)
    authorizeUri,       // brand-resolved
    authorizeUriForCa,
    accessTokenUri,     // brand-resolved
    accessTokenUriForCa,
    revokeAccessTokenUri,
    logoutActiveMemberUri,
    HPSRedirectUri,
    userInfoURL,
  };
};

// Differentiate origin types - Conversion from string to enum
export const getOriginType = (
  originType: string | undefined | null,
): ORIGIN_TYPES | null => {
  switch (originType?.toLowerCase()) {
    case ORIGIN_TYPES.BAM:
      return ORIGIN_TYPES.BAM;
    case ORIGIN_TYPES.PUBLICSITE:
      return ORIGIN_TYPES.PUBLICSITE;
    case ORIGIN_TYPES.GUEST:
      return ORIGIN_TYPES.GUEST;
    case ORIGIN_TYPES.MMA:
      return ORIGIN_TYPES.MMA;
    case ORIGIN_TYPES.BINDER_PAY_NOW:
      return ORIGIN_TYPES.BINDER_PAY_NOW;
    case ORIGIN_TYPES.BINDER_PAY_LATER:
      return ORIGIN_TYPES.BINDER_PAY_LATER;
    case ORIGIN_TYPES.CA:
      return ORIGIN_TYPES.CA;
    case ORIGIN_TYPES.CA_BINDER_PAY_LATER:
      return ORIGIN_TYPES.CA_BINDER_PAY_LATER;
    case ORIGIN_TYPES.GUEST_GOVERNMENT:
      return ORIGIN_TYPES.GUEST_GOVERNMENT;
    case ORIGIN_TYPES.ONE_TIME_GOVERNMENT_ACTIVE:
      return ORIGIN_TYPES.ONE_TIME_GOVERNMENT_ACTIVE;
    case ORIGIN_TYPES.BAM_GOVERNMENT_ACTIVE:
      return ORIGIN_TYPES.BAM_GOVERNMENT_ACTIVE;
    case ORIGIN_TYPES.NMA:
      return ORIGIN_TYPES.NMA;
    case ORIGIN_TYPES.UMA:
      return ORIGIN_TYPES.UMA;
    case ORIGIN_TYPES.BROKER:
      return ORIGIN_TYPES.BROKER;
    default:
      break;
  }
  return null;
};

export const closeOpenedTab = () => {
  window.opener = null;
  window.open('', '_self');
  window.close();
};

export const fetchUserTypeByOriginType = (
  origin: ORIGIN_TYPES,
  isMobileGovermentActiveUser?: boolean,
) => {
  switch (origin) {
    case ORIGIN_TYPES.MMA:
      return USER_TYPES.ONE_TIME_PAYMENT_ACTIVE_MEMBER;
    case ORIGIN_TYPES.NMA:
    case ORIGIN_TYPES.UMA:
      return isMobileGovermentActiveUser
        ? USER_TYPES.NMA_GOVT_USER
        : USER_TYPES.NMA_IFM_USER;
    default:
      return USER_TYPES.BAM_PUBLICSITE_ACTIVE_MEMBER;
  }
};

export const fetchServiceByOriginType = (origin: ORIGIN_TYPES) => {
  switch (origin) {
    case ORIGIN_TYPES.NMA:
      return SERVICE_TYPE.NMA;
    case ORIGIN_TYPES.UMA:
      return SERVICE_TYPE.MMA;
    default:
      return SERVICE_TYPE.MMA;
  }
};
